import { type DragEvent, useState } from 'react';

/** Moves the item at `from` so it ends up before the item that was at `to` (`to` = length: last). */
function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from + 1 === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to > from ? to - 1 : to, 0, item!);
  return next;
}

/**
 * Drag and drop reordering for a vertical list (native drag and drop, no library). Spread
 * `itemProps(i)` on each row; `dropLine(i)` says whether to draw the drop marker above (`before`)
 * or below (`after`) row `i`.
 */
/** `enabled: false` turns dragging off (read-only views). */
export function useSortable<T>(items: T[], onChange: (next: T[]) => void, enabled = true) {
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const reset = () => {
    setFrom(null);
    setTo(null);
  };
  return {
    dragging: from,
    itemProps: (i: number) =>
      !enabled
        ? {}
        : {
            draggable: true,
            onDragStart: (e: DragEvent) => {
              e.dataTransfer.effectAllowed = 'move';
              // Firefox needs some data to start a drag.
              e.dataTransfer.setData('text/plain', String(i));
              setFrom(i);
            },
            onDragOver: (e: DragEvent) => {
              if (from === null) return;
              e.preventDefault();
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setTo(e.clientY < rect.top + rect.height / 2 ? i : i + 1);
            },
            onDrop: (e: DragEvent) => {
              e.preventDefault();
              if (from !== null && to !== null) onChange(moveItem(items, from, to));
              reset();
            },
            onDragEnd: reset,
          },
    dropLine: (i: number): 'before' | 'after' | null => {
      if (from === null || to === null || to === from || to === from + 1) return null;
      if (to === i) return 'before';
      if (to === items.length && i === items.length - 1) return 'after';
      return null;
    },
  };
}
